import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { useFormContext } from '../../contexts/FormContext';          // Ensure correct import path
import { useFieldMetadata } from '../../contexts/FieldMetadataContext'; // Ensure correct import path

/**
 * Form-field selector used in several contexts.
 * • “basic-settings” → full-width outlined select with visible label.
 * • “rich-text-toolbar” (and anything else) → compact, borderless select.
 *
 * Only **UI** tweaks were made below:
 * 1. Stop the toolbar version from flex-growing: width set to “auto” + `flexShrink:0`.
 * 2. Force smaller font (≈10 px) in the option list via a shared `MenuProps`.
 *
 * No functional / data logic was altered.
 */
const FormFieldCustomSelectComponent = ({ value, onChange, context }) => {
  /* ------------------------------------------------------------------ */
  /* data                                                               */
  /* ------------------------------------------------------------------ */
  const { formId } = useFormContext();                 // current formId
  const { fieldsMetadata, isLoading } = useFieldMetadata();

  const [formFields, setFormFields] = useState([]);

  useEffect(() => {
    if (!formId || isLoading) return;

    // Transform metadata → [{label,value}, …] and filter out submit buttons
    const fieldsArray = Object.keys(fieldsMetadata)
      .map((fieldId) => {
        const fieldData = fieldsMetadata[fieldId];
        if (!fieldData || fieldData.type === 'submit') return null;

        return {
          label: fieldData.label || `Field ${fieldId}`,
          value: fieldData.ufi || fieldId,
        };
      })
      .filter(Boolean);

    setFormFields(fieldsArray);
  }, [formId, fieldsMetadata, isLoading]);

  /* ------------------------------------------------------------------ */
  /* shared style helpers                                               */
  /* ------------------------------------------------------------------ */

  /** Compact select shared by toolbar contexts */
  const baseSelectStyles = {
    minWidth: 135,
    maxWidth: 135,
    fontSize: '0.75rem',
    border: 'none',
    boxShadow: 'none',
    '& .MuiSelect-select': { padding: '4px 8px' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  };

  /** MenuProps used by every **compact** variant to ensure tiny option text */
  const smallMenuProps = {
    MenuListProps: { dense: true },
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: '0.75rem',      // ≈ 12 px
          minHeight: 24,
        },
        '& .MuiTypography-root': { fontSize: '0.625rem' },
      },
    },
  };

  /** style map by context */
  const selectStyles = {
    'basic-settings': {
      fullWidth: true,              // outlined, full-width
    },
    'rich-text-toolbar': { ...baseSelectStyles },
  };

  /* ------------------------------------------------------------------ */
  /* container box styles                                               */
  /* ------------------------------------------------------------------ */
  const containerBoxStyles =
    context === 'basic-settings'
      ? {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          width: '100%',            // still flex-grow here
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          width: 'auto',            // ← stops flex-growing inside toolbar
          flexShrink: 0,
        };

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <Box sx={containerBoxStyles}>
      {/* Label only in basic-settings context */}
      {context === 'basic-settings' && (
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', color: '#003459', mb: 0 }}
        >
          Disable field if dependency is blank:
        </Typography>
      )}

      {context === 'basic-settings' ? (
        /* -------------------------------------------------------------- */
        /* Outlined, full-width select with label                         */
        /* -------------------------------------------------------------- */
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Dependent field</InputLabel>
          <Select
            value={value || 'none'}
            onChange={(e) => {
              const selectedValue = e.target.value;
              const selectedField = formFields.find(
                (field) => field.value === selectedValue
              );
              onChange(
                selectedValue === 'none'
                  ? { label: 'None', value: null }
                  : {
                      label: selectedField?.label || 'None',
                      value: selectedField?.value || '',
                    }
              );
            }}
            displayEmpty
            input={<OutlinedInput label="Disabled unless" />}
            renderValue={(selected) =>
              selected === 'none'
                ? 'None'
                : formFields.find((f) => f.value === selected)?.label ||
                  'Select a field'
            }
            /* big menu keeps default typography – not modified */
          >
            <MenuItem value="none">None</MenuItem>

            {formFields.length > 0 ? (
              formFields.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No Fields Available
              </MenuItem>
            )}
          </Select>
        </FormControl>
      ) : (
        /* -------------------------------------------------------------- */
        /* Compact, borderless select for toolbars etc.                   */
        /* -------------------------------------------------------------- */
        <Select
          value={value}
          onChange={(e) => {
            const selectedField = formFields.find(
              (field) => field.value === e.target.value
            );
            onChange({
              label: selectedField.label,
              value: selectedField.value,
            });
          }}
          displayEmpty
          size="small"
          MenuProps={smallMenuProps}                 /* tiny option text */
          sx={selectStyles[context] || baseSelectStyles}
        >
          <MenuItem value="" disabled>
            {context === 'rich-text-toolbar'
              ? 'Select Field for Toolbar'
              : ' Form Fields'}
          </MenuItem>

          {formFields.length > 0 ? (
            formFields.map((field) => (
              <MenuItem key={field.value} value={field.value}>
                {field.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              No Fields Available
            </MenuItem>
          )}
        </Select>
      )}
    </Box>
  );
};

export default React.memo(FormFieldCustomSelectComponent);
